// S18 — the platform audit log (spec 15 §2.15, §2.15a, §4 S18; M15-9, D-622,
// D-623). Every entry of the platform chain newest first, filtered by staff
// member, action prefix, tenant and day; a row opens to its before, after and
// details; the whole filtered stream downloads as NDJSON. The chain check
// says how far the chain is proved intact, when it was checked and anchored,
// and a platform_admin can check it now. platform_admin and platform_readonly
// only; other roles are refused with a plain statement.
//
// The tenant filter comes from a tenant's own page (S10's link), as a chip
// naming the tenant, so no id is ever typed or shown.

import { useState, type ReactNode } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { Field } from "../../../components/field";
import { Select } from "../../../components/select";
import { ErrorState, ForbiddenState, TableSkeleton } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { formatTimestamp } from "../../../lib/datetime";
import { useDebouncedValue } from "../../../lib/use-debounced-value";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { useHeldKey, withHeldKey } from "../lib/held-key";
import { getTenant, listStaff, type PlatformMe } from "../lib/platform-api";
import {
  exportAuditLog,
  getChainVerification,
  listAuditLog,
  verifyChainNow,
  type AuditFilters,
  type AuditRow,
  type ChainVerification,
} from "../lib/support-api";
import { auditActor, canManageStaff, canReadAudit, dayBoundary } from "../lib/support";

const ACTION_PREFIX = /^[a-z0-9_.]+$/u;

export function AuditLogScreen({ me, tenantId }: { me: PlatformMe; tenantId?: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const allowed = canReadAudit(me.role);
  const [staffId, setStaffId] = useState("");
  const [prefixInput, setPrefixInput] = useState("");
  const [fromDay, setFromDay] = useState("");
  const [toDay, setToDay] = useState("");
  const prefix = useDebouncedValue(prefixInput.trim(), 300);
  const prefixValid = prefix === "" || ACTION_PREFIX.test(prefix);
  const from = fromDay === "" ? undefined : dayBoundary(fromDay, "from");
  const to = toDay === "" ? undefined : dayBoundary(toDay, "to");
  const filters: AuditFilters = {
    ...(staffId !== "" ? { staff_id: staffId } : {}),
    ...(prefix !== "" && prefixValid ? { action_prefix: prefix } : {}),
    ...(tenantId !== undefined ? { tenant_id: tenantId } : {}),
    ...(from !== undefined ? { from } : {}),
    ...(to !== undefined ? { to } : {}),
  };
  const filtered = Object.keys(filters).length > 0;

  const staff = useQuery({
    queryKey: ["platform", "staff"],
    queryFn: ({ signal }) => listStaff(signal),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });
  const tenant = useQuery({
    queryKey: ["platform", "tenant", tenantId ?? ""],
    queryFn: ({ signal }) => getTenant(tenantId ?? "", signal),
    enabled: allowed && tenantId !== undefined,
  });
  const log = useInfiniteQuery({
    queryKey: ["platform", "audit-log", filters],
    queryFn: ({ pageParam, signal }) => listAuditLog(filters, pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: allowed,
  });
  useSignInAgainOn401(log.error);
  const [exportError, setExportError] = useState(false);
  const exporter = useMutation({
    mutationFn: () => exportAuditLog(filters),
    onSuccess: (text) => {
      setExportError(false);
      const blob = new Blob([text], { type: "application/x-ndjson" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date();
      const day = [
        String(today.getFullYear()),
        String(today.getMonth() + 1).padStart(2, "0"),
        String(today.getDate()).padStart(2, "0"),
      ].join("-");
      a.download = `platform-audit-${day}.ndjson`;
      document.body.append(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: () => {
      setExportError(true);
    },
  });
  useSignInAgainOn401(exporter.error);

  if (!allowed) {
    return (
      <section className="admin-screen" data-testid="admin-audit-forbidden">
        <PageHeader title={t("admin.audit.title")} />
        <ForbiddenState
          title={t("admin.audit.forbiddenTitle")}
          body={t("admin.audit.forbiddenBody")}
        />
      </section>
    );
  }

  function who(row: AuditRow): string {
    const actor = auditActor(row.actor);
    if (actor.kind !== "staff") return t("admin.audit.automatic");
    const member = staff.data?.find((s) => s.id === actor.staffId);
    return member?.display_name ?? t("admin.audit.platformStaff");
  }

  const columns: DataTableColumn<AuditRow>[] = [
    {
      key: "when",
      header: t("admin.audit.colWhen"),
      render: (row) => formatTimestamp(row.occurred_at, i18n.language),
    },
    { key: "who", header: t("admin.audit.colWho"), render: who },
    {
      key: "action",
      header: t("admin.audit.colAction"),
      render: (row) => <code data-testid="admin-audit-action">{row.action}</code>,
    },
    {
      key: "tenant",
      header: t("admin.audit.colTenant"),
      render: (row) =>
        row.tenant_id === null ? (
          <span className="admin-muted">{t("admin.audit.noTenant")}</span>
        ) : row.tenant_id === tenantId && tenant.data !== undefined ? (
          tenant.data.tenant.name
        ) : (
          <Button
            size="sm"
            data-testid="admin-audit-tenant-filter"
            onClick={() => {
              void navigate({ to: "/console/audit", search: { tenant: row.tenant_id ?? "" } });
            }}
          >
            {t("admin.audit.filterTenant")}
          </Button>
        ),
    },
    {
      key: "entry",
      header: "#",
      numeric: true,
      render: (row) => row.id,
    },
  ];

  let body: ReactNode;
  if (log.isPending) {
    body = <TableSkeleton rows={8} cols={5} label={t("admin.audit.loading")} />;
  } else if (log.isError) {
    body = (
      <ErrorState
        title={t("admin.audit.loadFailed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void log.refetch();
        }}
        requestId={requestIdOf(log.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else {
    const rows = log.data.pages.flatMap((p) => p.items);
    body =
      rows.length === 0 ? (
        <p className="admin-muted" data-testid="admin-audit-empty">
          {filtered ? t("admin.audit.empty") : t("admin.audit.emptyAll")}
        </p>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => String(row.id)}
            empty={null}
            caption={t("admin.audit.title")}
            rowTestId="admin-audit-row"
            rowProps={(row) => ({ "data-action": row.action })}
            expansion={(row) => <AuditRowDetail row={row} />}
            expansionLabel={(row) => t("admin.audit.details", { id: row.id })}
          />
          {log.hasNextPage ? (
            <Button
              size="sm"
              busy={log.isFetchingNextPage}
              data-testid="admin-audit-more"
              onClick={() => {
                void log.fetchNextPage();
              }}
            >
              {t("admin.audit.more")}
            </Button>
          ) : null}
        </>
      );
  }

  return (
    <section className="admin-screen" data-testid="admin-audit-log">
      <PageHeader
        title={t("admin.audit.title")}
        actions={
          <Button
            size="sm"
            busy={exporter.isPending}
            busyLabel={t("admin.audit.exporting")}
            data-testid="admin-audit-export"
            onClick={() => {
              exporter.mutate();
            }}
          >
            {t("admin.audit.export")}
          </Button>
        }
      />
      <p className="admin-card-lead">{t("admin.audit.lead")}</p>
      <ChainCard me={me} />
      {exportError ? (
        <Banner kind="bad" data-testid="admin-audit-export-error">
          {t("admin.audit.exportFailed")}
        </Banner>
      ) : null}
      <div className="admin-filters">
        <Field label={t("admin.audit.staff")}>
          <Select
            value={staffId}
            data-testid="admin-audit-staff"
            onChange={(e) => {
              setStaffId(e.target.value);
            }}
          >
            <option value="">{t("admin.audit.anyStaff")}</option>
            {(staff.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.display_name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("admin.audit.action")} wide>
          <input
            className="ds-input"
            value={prefixInput}
            placeholder={t("admin.audit.actionPlaceholder")}
            data-testid="admin-audit-prefix"
            onChange={(e) => {
              setPrefixInput(e.target.value.toLowerCase());
            }}
          />
        </Field>
        <Field label={t("admin.audit.from")}>
          <input
            className="ds-input"
            type="date"
            value={fromDay}
            data-testid="admin-audit-from"
            onChange={(e) => {
              setFromDay(e.target.value);
            }}
          />
        </Field>
        <Field label={t("admin.audit.to")}>
          <input
            className="ds-input"
            type="date"
            value={toDay}
            data-testid="admin-audit-to"
            onChange={(e) => {
              setToDay(e.target.value);
            }}
          />
        </Field>
        {staffId !== "" || prefixInput !== "" || fromDay !== "" || toDay !== "" ? (
          <Button
            size="sm"
            data-testid="admin-audit-clear"
            onClick={() => {
              setStaffId("");
              setPrefixInput("");
              setFromDay("");
              setToDay("");
            }}
          >
            {t("admin.audit.clear")}
          </Button>
        ) : null}
      </div>
      {!prefixValid ? (
        <Banner kind="warn" data-testid="admin-audit-prefix-invalid">
          {t("admin.audit.actionInvalid")}
        </Banner>
      ) : null}
      {tenantId !== undefined ? (
        <div className="admin-inline" data-testid="admin-audit-tenant-chip">
          <StatusChip variant="accent" dot={false}>
            {t("admin.audit.tenantFilter", { name: tenant.data?.tenant.name ?? "…" })}
          </StatusChip>
          <Button
            size="sm"
            data-testid="admin-audit-tenant-clear"
            onClick={() => {
              void navigate({ to: "/console/audit", search: {} });
            }}
          >
            {t("admin.audit.removeTenant")}
          </Button>
        </div>
      ) : null}
      {body}
    </section>
  );
}

function AuditJson({ label, value }: { label: string; value: unknown }) {
  const { t } = useTranslation();
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === "object" && Object.keys(value).length === 0);
  return (
    <div className="admin-audit-part">
      <span className="admin-fact-k">{label}</span>
      {empty ? (
        <span className="admin-muted">{t("admin.audit.nothing")}</span>
      ) : (
        <pre className="admin-audit-json">{JSON.stringify(value, null, 2)}</pre>
      )}
    </div>
  );
}

function AuditRowDetail({ row }: { row: AuditRow }) {
  const { t } = useTranslation();
  return (
    <div className="admin-audit-detail" data-testid="admin-audit-detail">
      <AuditJson label={t("admin.audit.before")} value={row.before_state} />
      <AuditJson label={t("admin.audit.after")} value={row.after_state} />
      <AuditJson label={t("admin.audit.metadata")} value={row.metadata} />
    </div>
  );
}

function ChainCard({ me }: { me: PlatformMe }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const key = useHeldKey();
  const verification = useQuery({
    queryKey: ["platform", "audit-chain"],
    queryFn: ({ signal }) => getChainVerification(signal),
  });
  useSignInAgainOn401(verification.error);
  const verify = useMutation({
    mutationFn: () => withHeldKey(key, { verify: true }, (k) => verifyChainNow(k)),
    onSuccess: (next) => {
      queryClient.setQueryData(["platform", "audit-chain"], next);
      void queryClient.invalidateQueries({ queryKey: ["platform", "audit-log"] });
    },
  });
  useSignInAgainOn401(verify.error);

  function line(v: ChainVerification): ReactNode {
    if (v.verdict === "never_verified" || v.verified_at === null) {
      return t("admin.audit.neverVerified");
    }
    const when = formatTimestamp(v.verified_at, i18n.language);
    const verdict =
      v.verdict === "broken" && v.break_at_id !== null && v.break_reason !== null
        ? t("admin.audit.broken", {
            id: v.break_at_id,
            reason: t(`admin.audit.breakReason.${v.break_reason}`),
            when,
          })
        : t("admin.audit.intact", { id: v.intact_through_id ?? 0, when });
    const anchor =
      v.anchored_at !== null
        ? t("admin.audit.anchored", { when: formatTimestamp(v.anchored_at, i18n.language) })
        : v.anchor_error === "no_anchor_store" || v.anchor_error === "chain_broken"
          ? t(`admin.audit.notAnchored.${v.anchor_error}`)
          : t("admin.audit.notAnchored.put_failed");
    return `${verdict} ${anchor}`;
  }

  let body: ReactNode;
  if (verification.isPending) {
    body = <p className="admin-muted">{t("admin.audit.loading")}</p>;
  } else if (verification.isError) {
    body = (
      <ErrorState
        title={t("admin.audit.loadFailed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void verification.refetch();
        }}
        requestId={requestIdOf(verification.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else {
    const v = verification.data;
    body = (
      <p className="admin-card-lead" data-testid="admin-audit-chain-line" data-verdict={v.verdict}>
        <StatusChip
          variant={v.verdict === "intact" ? "ok" : v.verdict === "broken" ? "bad" : "neutral"}
          data-testid="admin-audit-chain-verdict"
        >
          {t(`admin.audit.verdict.${v.verdict}`)}
        </StatusChip>{" "}
        {line(v)}
      </p>
    );
  }
  return (
    <Card
      title={t("admin.audit.chainTitle")}
      data-testid="admin-audit-chain"
      action={
        canManageStaff(me.role) ? (
          <Button
            size="sm"
            busy={verify.isPending}
            busyLabel={t("admin.audit.verifying")}
            data-testid="admin-audit-verify"
            onClick={() => {
              verify.mutate();
            }}
          >
            {t("admin.audit.verifyNow")}
          </Button>
        ) : undefined
      }
    >
      {body}
      {verify.isError ? (
        <Banner kind="bad" data-testid="admin-audit-verify-error">
          {t("admin.audit.verifyFailed")}
        </Banner>
      ) : null}
    </Card>
  );
}
